import { useEffect } from "react";
import type { ThemeManifest } from "@crashx/theme-schema";
import { useRoundStore } from "../../state/roundStore.js";
import { useConnectionStore } from "../../state/connectionStore.js";
import { serverNow } from "../../net/connection.js";
import { audioEngine } from "../../game/audio/AudioEngine.js";
import { useAudioStore } from "../../game/audio/audioStore.js";
import { newSettlements } from "./settlements.js";
import { useNotificationStore } from "./notificationStore.js";

export function useGameFeedback(theme: ThemeManifest | null): void {
  const muted = useAudioStore((state) => state.muted);
  useEffect(() => {
    if (!theme) return;
    audioEngine.configure(theme);
    return () => audioEngine.stopAll();
  }, [theme]);
  useEffect(() => {
    audioEngine.setMuted(muted);
    const engine = () => {
      if (
        useRoundStore.getState().round?.phase === "FLYING" &&
        useConnectionStore.getState().status === "connected"
      )
        audioEngine.play("engine");
    };
    const gesture = () => {
      if (!muted) void audioEngine.unlock().then(engine);
    };
    window.addEventListener("pointerdown", gesture);
    window.addEventListener("keydown", gesture);
    engine();
    return () => {
      window.removeEventListener("pointerdown", gesture);
      window.removeEventListener("keydown", gesture);
    };
  }, [muted, theme]);
  useEffect(() => {
    const seen = new Set<string>();
    const unsubscribe = useRoundStore.subscribe((current, previous) => {
      for (const bet of newSettlements(previous, current)) {
        if (seen.has(bet.betId)) continue;
        seen.add(bet.betId);
        if (seen.size > 256) {
          const oldest = seen.values().next().value;
          if (oldest) seen.delete(oldest);
        }
        useNotificationStore.getState().push(bet);
        if (bet.status === "won") audioEngine.play("cashout");
      }
      if (current.round?.phase === previous.round?.phase) return;
      if (current.round?.phase !== "FLYING") audioEngine.stop("engine");
      if (current.lastSyncAt !== previous.lastSyncAt) return;
      if (current.round?.phase === "FLYING") {
        audioEngine.play("takeoff");
        audioEngine.play("engine");
      }
      if (current.round?.phase === "CRASHED") audioEngine.play("crash");
    });
    const disconnect = useConnectionStore.subscribe((state) => {
      if (state.status !== "connected") audioEngine.stopAll();
    });
    let second = -1;
    const timer = setInterval(() => {
      const round = useRoundStore.getState().round;
      if (
        round?.phase !== "BETTING" ||
        round.bettingEndsAt === null ||
        useConnectionStore.getState().status !== "connected"
      )
        return;
      const remaining = Math.ceil((round.bettingEndsAt - serverNow()) / 1000);
      if (remaining !== second && remaining > 0 && remaining <= 3) audioEngine.play("tick");
      second = remaining;
    }, 250);
    return () => {
      unsubscribe();
      disconnect();
      clearInterval(timer);
      audioEngine.stopAll();
    };
  }, []);
}
